import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Button } from 'src/UIKit/components';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDeleteUserApi, useGetUsersApi } from 'src/api/users';
import { useGetRolesApi } from 'src/modules/profile/api';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { getDateTimeUSAFormatWithTime } from 'src/constants/helpers';
import { User, Role } from 'src/constants/types';
import { isActionAllowed } from 'src/helpers/permissions';
import { getSortFieldForApi } from 'src/helpers/sort';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { selectProfile } from 'src/modules/profile/slices';
import { GetUsersParams } from 'src/api/users/types';
import { itemsPerPage } from './constants';
import InviteUserModal from './InviteUserModal/view';
import { Box, IconButton, Stack } from '@mui/material';
import GridData from 'src/components/GridData/GridData';
import { Dropdown } from 'src/UIKit';
import { TableHeader, TableHeaderItem } from 'src/components/TableHeader/TableHeader';
import TooltipCell from 'src/components/TooltipCell';
import { GridPaginationModel, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { settingsTabPanelSx } from '../../helpers';

const UserManagment = () => {
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState<string>('firstName_desc');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [showInviteUserModal, setInviteUserModal] = useState(false);
  const [users, setUsers] = useState<{ count: number; items: User[] | null }>({
    count: 0,
    items: null,
  });
  const [editableUser, setEditableUser] = useState<User | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const selectedCompanyId: string | undefined = useSelector(getCurrentCompanyId);
  const deleteUser = useDeleteUserApi();
  const getRoles = useGetRolesApi();
  const { profile } = useSelector(selectProfile);
  const profileRole = useMemo(() => (profile?.role ? profile.role.name : 'bhi_admin'), [profile]);
  const isBhiAdmin = profileRole === 'bhi_admin';
  const handleUserEdit = (userId: string) => {
    setInviteUserModal(true);
    setEditableUser(users.items?.find(({ id }) => id === userId));
  };
  const handleUserDelete = (id: string) => {
    selectedCompanyId && deleteUser(id, selectedCompanyId).then(() => refreshUsers(selectedRole));
  };

  const { openDialog, ConfirmationDialog } = useConfirmationDialog();
  const getUsers = useGetUsersApi();

  const fetchRoles = () => {
    getRoles()
      .then(({ data }) => {
        setRoles(data);
      })
      .catch(e => {
        console.log(e);
      });
  };

  const refreshUsers = (roleId?: string) => {
    setEditableUser(undefined);
    if (selectedCompanyId) {
      setIsLoading(true);
      const sort = getSortFieldForApi(sortField);
      const reqQuery: GetUsersParams = {
        companyId: selectedCompanyId,
        limit: itemsPerPage,
        offset: page * itemsPerPage,
        sort: sort,
      };
      if (searchQuery) {
        reqQuery.query = searchQuery;
      }
      if (roleId && roleId !== 'all') {
        reqQuery.roleId = roleId;
      }

      getUsers(reqQuery)
        .then(({ data }) => {
          setUsers(data);
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

    useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => { 
    selectedCompanyId && refreshUsers(selectedRole);
  }, [page, selectedCompanyId, sortField, searchQuery, selectedRole]);

  useEffect(() => {
    setPage(0);
  }, [sortField, searchQuery, selectedRole]);

  const formatTableRows = (users: User[] | null) => {
    return (
      users?.map(({ id, firstName, lastName, phoneNumber, email, role, lastLoginDate }: User) => {
        return {
          id,
          firstName,
          lastName,
          email,
          phoneNumber,
          role: role.title,
          roleName: role.name,
          lastLogin: lastLoginDate,
        };
      }) || []
    );
  };

  // Get unique roles for the dropdown
  const roleOptions = useMemo(() => {
    const roleDropdownOptions = roles.map(role => ({
      value: role.id,
      label: role.title,
      title: role.title,
    })).sort((a, b) => a.label.localeCompare(b.label));
    
    return [
      { value: 'all', label: 'All Roles', title: 'All Roles' },
      ...roleDropdownOptions,
    ];
  }, [roles]);



  return (
    <>
      <InviteUserModal
        show={showInviteUserModal}
        setShowModal={setInviteUserModal}
        userData={editableUser}
        refresh={() => refreshUsers(selectedRole)}
      />

      <Box sx={settingsTabPanelSx}>
        <GridData
          header={
            <TableHeader
              searchValue={searchQuery}
              onSearchChange={(_: ChangeEvent<HTMLInputElement>, query: string) => {
                setSearchQuery(query);
              }}
              filters={
                <TableHeaderItem>
                  <Dropdown
                    options={roleOptions}
                    value={selectedRole}
                    onChange={(_: ChangeEvent<HTMLInputElement>, value: string) => {
                      setSelectedRole(value);
                      refreshUsers(value);
                    }}
                    placeholder="Select Role"
                    margin="none"
                  />
                </TableHeaderItem>
              }
              actions={
                <Button
                  variant="contained"
                  onClick={() => {
                    setInviteUserModal(true);
                    setEditableUser(undefined);
                  }}
                  disabled={!isActionAllowed({ authorRole: profileRole })}
                >
                  Invite User
                </Button>
              }
            />
          }
          isLoading={isLoading}
          rows={formatTableRows(users.items)}
          columns={[
            {
              field: 'firstName',
              headerName: 'First Name',
              minWidth: 200,
              editable: false,
              flex: 1,
              renderCell: params => {
                return (
                  <Stack direction="row" alignItems="center" height="100%">
                    <TooltipCell value={params.value || ''} variant="body2" />
                  </Stack>
                );
              },
            },
            {
              field: 'lastName',
              headerName: 'Last Name',
              minWidth: 200,
              editable: false,
              flex: 1,
              renderCell: params => {
                return (
                  <Stack direction="row" alignItems="center" height="100%">
                    <TooltipCell value={params.value || ''} variant="body2" />
                  </Stack>
                );
              },
            },
            {
              field: 'email',
              headerName: 'Email',
              minWidth: 100,
              editable: false,
              flex: 1,
              renderCell: params => {
                return (
                  <Stack direction="row" alignItems="center" height="100%">
                    <TooltipCell value={params.value || ''} variant="body2" />
                  </Stack>
                );
              },
            },
            {
              field: 'phoneNumber',
              headerName: 'Phone Number',
              minWidth: 100,
              editable: false,
              flex: 1,
              renderCell: params => {
                return (
                  <Stack direction="row" alignItems="center" height="100%">
                    <TooltipCell value={params.value || ''} variant="body2" />
                  </Stack>
                );
              },
            },
            {
              field: 'role',
              headerName: 'Role',
              minWidth: 100,
              editable: false,
              flex: 1,
              renderCell: params => {
                return (
                  <Stack direction="row" alignItems="center" height="100%">
                    <TooltipCell value={params.value || ''} variant="body2" />
                  </Stack>
                );
              },
            },
            ...(isBhiAdmin
              ? [
                  {
                    field: 'lastLogin',
                    headerName: 'Last Login',
                    minWidth: 160,
                    editable: false,
                    flex: 1,
                    renderCell: (params: GridRenderCellParams) => {
                      const formatted = params.value
                        ? getDateTimeUSAFormatWithTime(params.value)
                        : '';
                      return (
                        <Stack direction="row" alignItems="center" height="100%">
                          <TooltipCell value={formatted} variant="body2" />
                        </Stack>
                      );
                    },
                  },
                ]
              : []),
          ]}
          renderAction={r => {
            return (
              <>
                <IconButton
                  disabled={
                    !isActionAllowed({
                      authorRole: profileRole,
                      userRole: r.roleName,
                    }) || r.id === profile?.id
                  }
                  size="small"
                  color="primary"
                  onClick={() => handleUserEdit(r.id)}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                  disabled={
                    !isActionAllowed({
                      authorRole: profileRole,
                      userRole: r.roleName,
                    }) || r.id === profile?.id
                  }
                  size="small"
                  color="primary"
                  onClick={() =>
                    openDialog({
                      title: `Delete "${r.firstName} ${r.lastName}"`,
                      content: 'Are you sure you want to delete this user?',
                      onConfirm: () => handleUserDelete(r.id),
                    })
                  }
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </>
            );
          }}
          sorting={{
            value: sortField,
            onChange: (s: GridSortModel) => {
              if (s[0]) {
                setSortField(`${s[0]?.field}_${s[0]?.sort}`);
              }
            },
          }}
          pagination={{
            page: page,
            pageSize: itemsPerPage,
            totalCount: users.count || 0,
            onChange: (p: GridPaginationModel) => {
              setPage(p.page);
            },
          }}
        />
      </Box>
      <ConfirmationDialog />
    </>
  );
};

export default UserManagment;
